/**
 * Phase 28 — a verdict's flat rule (ALL/ANY of conditions, each maybe NOT)
 * as the Flow Optimizer's conditions list draws it, and back. Pure: the
 * rows keep the day aggregate's units, the lines show the operator's (a 0–1
 * rate in percent). Phase 38: a tag row ("the list is tagged X") is a tag
 * leaf, both ways.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenVerdictSource } from '@trafficpulse/shared';

import type { ExcludeViewItem, LeafRuleNode } from '@/features/flow-optimizer/utils/exclude-view-model';

import type { DraftFlatRule, DraftVerdictRow } from '../../../../drop-generator.types';
import { isTagRow } from '../../../../drop-generator-rule.types';
import type { RuleLines } from '../../rules/rule-model';
import { METRIC_MEANING } from '../condition/metric-meanings';
import { onWords } from '../source/source-words';
import { fromInputValue, toInputValue } from './gate-metric-catalog';

type Condition = Extract<LeafRuleNode, { kind: 'condition' }>;
type TagLeaf = { kind: 'tag'; tagName: string; negate?: boolean };

const leafOf = (row: DraftVerdictRow): LeafRuleNode =>
  (isTagRow(row)
    ? { kind: 'tag', tagName: row.tag, ...(row.negate ? { negate: true } : {}) }
    : {
        kind: 'condition',
        metric: row.metric,
        op: row.op,
        value: toInputValue(row.metric, row.value),
      }) as unknown as LeafRuleNode;

export function flatToLines(rule: Pick<DraftFlatRule, 'join' | 'rows'>): RuleLines {
  return {
    op: rule.join,
    items: rule.rows.map((row, i) => (row.not ? { kind: 'not', id: `v${i}`, child: leafOf(row) } : { kind: 'leaf', id: `v${i}`, node: leafOf(row) })),
  };
}

const rowOf = (node: LeafRuleNode, not: boolean): DraftVerdictRow | null => {
  if (node.kind === 'tag') {
    const t = node as unknown as TagLeaf;
    return {
      tag: t.tagName,
      ...(t.negate ? { negate: true } : {}),
      ...(not ? { not: true } : {}),
    };
  }
  if (node.kind !== 'condition') return null;
  const c = node as unknown as Condition;
  return {
    metric: String(c.metric),
    op: c.op,
    value: fromInputValue(String(c.metric), c.value),
    ...(not ? { not: true } : {}),
  };
};

/** Thresholds and tag rows come back — the verdicts judge no ranking. */
export function linesToFlat(lines: RuleLines): Pick<DraftFlatRule, 'join' | 'rows'> {
  return {
    join: lines.op,
    rows: lines.items.flatMap((it) => {
      const row = rowOf(it.kind === 'leaf' ? it.node : it.child, it.kind === 'not');
      return row ? [row] : [];
    }),
  };
}

export const newVerdictLine = (starter: DraftVerdictRow, not: boolean, i: number): ExcludeViewItem => (not ? { kind: 'not', id: `v${i}`, child: leafOf(starter) } : { kind: 'leaf', id: `v${i}`, node: leafOf(starter) });

const OP_WORDS: Record<string, string> = {
  '<': 'under',
  '<=': 'at most',
  '=': 'exactly',
  '!=': 'not',
  '>': 'over',
  '>=': 'at least',
};

/** Phase 39 — the words a card's source brings to a row's hover. */
export interface SourceWording {
  source: DropGenVerdictSource;
  /** "any of today's drops" · "the drop" · "the list of the day" · "the gateway". */
  subject: string;
  meaningOf: (id: string) => string | undefined;
}

const DROPS: SourceWording = {
  source: { kind: 'drops' },
  subject: 'the list of the day',
  meaningOf: (id) => METRIC_MEANING[id],
};

/** The row's hover, in the verdict's words (the Flow Optimizer's say "keep their weight"). */
export function verdictConditionWords(
  node: {
    kind: string;
    metric?: unknown;
    op?: string;
    value?: unknown;
    tagName?: unknown;
    negate?: unknown;
  },
  labelOf: (id: string) => string,
  wording: SourceWording = DROPS,
): string | null {
  if (node.kind === 'tag') return `This condition holds when ${wording.subject} ${node.negate ? 'is not' : 'is'} tagged "${String(node.tagName)}" (a Tag Rules tag). One the tag cannot judge is unknown — and unknown never acts.`;
  if (node.kind !== 'condition' || typeof node.op !== 'string') return null;
  const label = labelOf(String(node.metric));
  const meaning = wording.meaningOf(String(node.metric));
  return `This condition holds when ${label} is ${OP_WORDS[node.op] ?? node.op} ${String(node.value)} — ${onWords(wording.source)}. ${meaning ? `${label} = ${meaning}.` : ''}`.trim();
}
